'use client'

import * as React from 'react'
import { Mic } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

// Web Speech API constructor, when the browser has one. Reality check:
// Chrome/Android solid (server-backed, cs-CZ supported); iOS Safari 14.5+
// works but is flaky; Firefox absent. Strictly a progressive enhancement —
// render the mic only when this returns non-null, and every failure path
// degrades silently to the typed flow.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSpeechRecognition(): (new () => any) | null {
  if (typeof window === 'undefined') return null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

// Shared dictation engine for every search input (mobile sheet, desktop
// header input, landing hero input). One utterance per press (continuous:
// false — auto-ends on silence); interim results stream into the field so
// the user sees words land; dictation APPENDS comma-joined so it composes
// with chips and typing. Recognition language follows the UI language (the
// same rule the query chips use: Czech users compose Czech queries).
export function useDictation(getValue: () => string, setValue: (v: string) => void) {
  const { uiLanguage } = useI18n()
  const [listening, setListening] = React.useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = React.useRef<any>(null)
  const baseRef = React.useRef('')
  // Two-pass on purpose: `supported` must be false on the server AND on the
  // first client render (they must match — computing it inline from `window`
  // caused a hydration mismatch on the SSR'd landing/header inputs). The mic
  // appears right after mount on capable browsers.
  const [supported, setSupported] = React.useState(false)
  React.useEffect(() => {
    setSupported(!!getSpeechRecognition())
  }, [])

  const stop = React.useCallback(() => {
    recognitionRef.current?.stop()
    recognitionRef.current = null
    setListening(false)
  }, [])

  const start = () => {
    const Ctor = getSpeechRecognition()
    if (!Ctor || listening) return
    const rec = new Ctor()
    recognitionRef.current = rec
    const current = getValue()
    baseRef.current = current.trim() ? `${current.replace(/[,\s]+$/, '')}, ` : ''
    rec.lang = uiLanguage === 'cs' ? 'cs-CZ' : 'en-US'
    rec.interimResults = true
    rec.continuous = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onresult = (e: any) => {
      let transcript = ''
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript
      setValue(baseRef.current + transcript.trim())
    }
    rec.onend = () => {
      recognitionRef.current = null
      setListening(false)
    }
    // Denied permission / no speech / network hiccup — degrade silently to
    // typing, never a modal error (iOS Safari flakiness).
    rec.onerror = rec.onend
    try {
      rec.start()
      setListening(true)
    } catch {
      recognitionRef.current = null
    }
  }

  // Never leave the mic hot on unmount.
  React.useEffect(() => stop, [stop])

  const toggle = () => (listening ? stop() : start())

  return { supported, listening, start, stop, toggle }
}

// The mic control — identical affordance everywhere: idle muted, listening =
// primary tint + pulse. Callers size it via className (padding) and
// iconClassName.
export function DictationMicButton({
  listening,
  onToggle,
  className,
  iconClassName,
}: {
  listening: boolean
  onToggle: () => void
  className?: string
  iconClassName?: string
}) {
  const { t } = useI18n()
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={
        listening ? t('search.voiceStop', 'Stop dictation') : t('search.voiceStart', 'Search by voice')
      }
      className={cn(
        'rounded-full transition-colors',
        listening ? 'animate-pulse bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
        className
      )}
    >
      <Mic className={cn('h-5 w-5', iconClassName)} />
    </button>
  )
}
